import React, { useEffect, useState } from "react";
import Main from "../Components/Main";
import PageContainer from "../Components/PageContainer";
import CollapseCard from "../Components/CollapseCard";
import VehicleAdvanceForm from "../Components/VehicleAdvanceForm";
import { useToast } from "../Components/toast/ToastProvider";
import {
  getVehicleAdvanceByDate,
  saveVehicleAdvance,
} from "../Service/VehicleAdvanceService";
import { getAllVehicles } from "../Service/VehicleService";

const VehicleAdvance = () => {
  const [date, setDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  const emptyRow = {
    vehicle_id: "",
    vehicle_number: "",
    amount: "",
    filteredVehicle: [],
    highlightedVehicleIndex: -1,
    showVehicleList: false,
  };

  const [rows, setRows] = useState(
    Array.from({ length: 70 }, () => ({ ...emptyRow })),
  );

  const [vehicles, setVehicles] = useState([]);
  const toast = useToast();

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    if (date && vehicles.length > 0) {
      fetchAllVehiclesAdvance(date);
    }
  }, [date, vehicles]);

  const fetchVehicles = async () => {
    try {
      const res = await getAllVehicles();
      setVehicles(res.data);
    } catch (err) {
      toast(err.message || "Failed to Fetch Vehicles", "ERROR");
    }
  };

  const fetchAllVehiclesAdvance = async (selectedDate) => {
    try {
      const res = await getVehicleAdvanceByDate(selectedDate);
      const data = res.data || [];

      if (data.length === 0) {
        setRows(Array.from({ length: 70 }, () => ({ ...emptyRow })));
        return;
      }

      const filledRows = data.map((adv) => {
        const vehicle = vehicles.find((v) => v.id === adv.vehicle_id);
        return {
          vehicle_id: adv.vehicle_id,
          vehicle_number: vehicle ? vehicle.vehicle_number : "",
          amount: Number(adv.amount).toFixed(2),
          filteredVehicle: [],
          highlightedVehicleIndex: -1,
          showVehicleList: false,
        };
      });
      const paddedRows = [
        ...filledRows,
        ...Array.from({ length: 70 - filledRows.length }, () => ({
          ...emptyRow,
        })),
      ];

      setRows(paddedRows);
    } catch (err) {
      toast(err.message || "Failed to Fetch VehicleAdvance", "ERROR");
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
  };

  // const handleAddRows = () => {
  //   setRows([
  //     ...rows,
  //     {
  //       vehicle_id: "",
  //       vehicle_number: "",
  //       amount: "",
  //       filteredVehicle: [],
  //       highlightedVehicleIndex: -1,
  //       showVehicleList: false,
  //     },
  //   ]);
  // };

  const handleVehicleChange = (index, e) => {
    const input = e.target.value;
    const updated = [...rows];
    updated[index].vehicle_number = input;
    if (input.trim() === "") {
      updated[index].filteredVehicle = [];
      updated[index].showVehicleList = false;
      updated[index].vehicle_id = "";
      updated[index].highlightedVehicleIndex = -1;

      setRows(updated);
      return;
    }

    const filtered = vehicles.filter((v) =>
      v.vehicle_number.toLowerCase().includes(input.toLowerCase()),
    );
    updated[index].filteredVehicle = filtered;
    updated[index].showVehicleList = true;
    updated[index].highlightedVehicleIndex = -1;

    const existingVehicle = vehicles.find(
      (v) => v.vehicle_number.toLowerCase() === input.toLowerCase(),
    );
    updated[index].vehicle_id = existingVehicle ? existingVehicle.id : "";

    setRows(updated);
  };

  const handleVehicleSelect = (index, vehicle) => {
    const updated = [...rows];
    updated[index].vehicle_number = vehicle.vehicle_number;
    updated[index].vehicle_id = vehicle.id;
    updated[index].filteredVehicle = [];
    updated[index].showVehicleList = false;
    updated[index].highlightedVehicleIndex = -1;
    setRows(updated);

    setTimeout(() => focusAmountField(index), 70);
  };

  const focusNextField = (index) => {
    const nextIndex = index + 1;
    if (nextIndex >= rows.length) return;

    const nextInput = document.getElementById(`vehicle-${nextIndex}`);
    if (nextInput) nextInput.focus();
  };

  const focusAmountField = (index) => {
    const amountInput = document.getElementById(`amount-${index}`);
    if (amountInput) amountInput.focus();
  };

  const handleKeyDown = (index, e) => {
    const updated = [...rows];
    const { filteredVehicle, highlightedVehicleIndex, showVehicleList } =
      updated[index];

    if (showVehicleList && filteredVehicle.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        updated[index].highlightedVehicleIndex =
          highlightedVehicleIndex < filteredVehicle.length - 1
            ? highlightedVehicleIndex + 1
            : 0;
        setRows(updated);
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        updated[index].highlightedVehicleIndex =
          highlightedVehicleIndex > 0
            ? highlightedVehicleIndex - 1
            : filteredVehicle.length - 1;
        setRows(updated);
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedVehicleIndex >= 0) {
          handleVehicleSelect(index, filteredVehicle[highlightedVehicleIndex]);
          return;
        }
      }
    }

    if (e.key === "Enter") {
      e.preventDefault();
      focusNextField(index);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const records = rows
        .filter((r) => r.vehicle_id && r.amount !== "")
        .map((r) => ({ ...r, date, amount: Number(r.amount).toFixed(2) }));

      if (records.length === 0) {
        toast("Please fill all fields", "ERROR");
        return;
      }
      await saveVehicleAdvance(records);
      toast("Vehicle advance saved successfully", "SUCCESS");
      const updated = rows.map((r) => {
        const found = records.find((rec) => rec.vehicle_id === r.vehicle_id);
        return found ? { ...r, amount: found.amount } : r;
      });
      setRows(updated);
    } catch (err) {
      console.error(err);
      toast("Failed to save record", "ERROR");
    }
  };

  return (
    <Main>
      <PageContainer>
        <CollapseCard title="Vehicle Advance">
          <VehicleAdvanceForm
            setDate={setDate}
            date={date}
            // handleAddRows={handleAddRows}
            handleChange={handleChange}
            rows={rows}
            handleSave={handleSave}
            handleVehicleChange={handleVehicleChange}
            handleVehicleSelect={handleVehicleSelect}
            handleKeyDown={handleKeyDown}
          />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default VehicleAdvance;
